import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useAuth } from './AuthContext';
import NotificacoesService from '../services/notificacoesService';
import SessoesService from '../services/sessoesService';
import { AppContextType } from '../types';

const AppContext = createContext<AppContextType | undefined>(undefined);

export const useApp = () => {
  const context = useContext(AppContext);
  if (context === undefined) {
    throw new Error('useApp must be used within an AppProvider');
  }
  return context;
};

interface AppProviderProps {
  children: ReactNode;
}

export const AppProvider: React.FC<AppProviderProps> = ({ children }) => {
  const { user } = useAuth();
  // Inicializar filial atual com a primeira filial do usuário, ou FILIAL_1 como fallback
  const filialInicial = user?.filiais && user.filiais.length > 0 ? user.filiais[0] : 'FILIAL_1';
  const [filialAtual, setFilialAtual] = useState<string>(filialInicial);
  const [notificacoesNaoLidas, setNotificacoesNaoLidas] = useState<number>(0);
  const [subscriptionId, setSubscriptionId] = useState<string | null>(null);

  // Atualizar filial atual quando o usuário mudar (logout/login)
  useEffect(() => {
    if (user?.filiais && user.filiais.length > 0) {
      setFilialAtual(user.filiais[0]);
    } else {
      setFilialAtual('FILIAL_1');
    }
  }, [user]);

  // Função para validar e mudar a filial atual
  const setFilialAtualComValidacao = (novaFilial: string) => {
    // Se o usuário for admin ou gerente, pode mudar para qualquer filial
    if (user?.perfil === 'admin' || user?.perfil === 'gerente') {
      setFilialAtual(novaFilial);
      return;
    }

    // Para outros perfis, verificar se a filial está nas filiais permitidas
    if (user?.filiais && user.filiais.includes(novaFilial)) {
      setFilialAtual(novaFilial);
    } else {
      console.warn('Usuário não tem permissão para acessar esta filial:', novaFilial);
      alert('Você não tem permissão para acessar esta filial. Entre em contato com o administrador.');
    }
  };

  // Função para tocar som de notificação
  const tocarSomNotificacao = () => {
    try {
      const audio = new Audio('/notification.mp3');
      audio.volume = 0.5;
      
      // Carregar o áudio antes de tocar
      audio.load();
      
      // Tocar som
      const playPromise = audio.play();
      
      if (playPromise !== undefined) {
        playPromise.catch(err => {
          console.error('Erro ao tocar som:', err);
        });
      }
    } catch (error) {
      console.error('Erro ao tocar som de notificação:', error);
    }
  };

  useEffect(() => {
    if (!user) return;

    // Carregar notificações não lidas
    const loadNotificacoes = async () => {
      try {
        const count = await NotificacoesService.contarNaoLidas(user.id);
        setNotificacoesNaoLidas(count);
      } catch (error) {
        console.error('Erro ao carregar notificações:', error);
      }
    };

    loadNotificacoes();

    // Configurar subscription para notificações em tempo real
    const subId = NotificacoesService.inscrever(user.id, (notificacao) => {
      setNotificacoesNaoLidas(prev => prev + 1);
      tocarSomNotificacao();
    });

    setSubscriptionId(subId);

    // 🚨 TEMPORARIAMENTE DESABILITADO: Polling periódico para evitar rate limiting
    const pollingInterval = setInterval(async () => {
      if (!user) return;
      try {
        const count = await NotificacoesService.contarNaoLidas(user.id);
        setNotificacoesNaoLidas(prev => {
          if (count > prev) {
            tocarSomNotificacao();
          }
          return count;
        });
      } catch (error) {
        console.error('❌ Erro no polling de notificações:', error);
      }
    }, 60000); // 60 segundos

    // Atualizar atividade do usuário a cada 5 minutos
    const atividadeInterval = setInterval(async () => {
      try {
        await SessoesService.atualizarAtividade(user.id, filialAtual);
      } catch (error) {
        console.error('Erro ao atualizar atividade:', error);
      }
    }, 5 * 60 * 1000); // 5 minutos

    return () => {
      if (subId) {
        NotificacoesService.desinscrever(subId);
      }
      if (pollingInterval) {
        clearInterval(pollingInterval);
      }
      if (atividadeInterval) {
        clearInterval(atividadeInterval);
      }
    };
  }, [user, filialAtual]);

  const refreshNotificacoes = async () => {
    if (!user) return;
    try {
      const count = await NotificacoesService.contarNaoLidas(user.id);
      setNotificacoesNaoLidas(count);
    } catch (error) {
      console.error('Erro ao atualizar notificações:', error);
    }
  };

  const value: AppContextType = {
    filialAtual,
    setFilialAtual: setFilialAtualComValidacao,
    setFilialAtualComValidacao,
    setFilialAtualSemValidacao: setFilialAtual,
    notificacoesNaoLidas,
    refreshNotificacoes
  };

  return (
    <AppContext.Provider value={value}>
      {children}
    </AppContext.Provider>
  );
};

export default AppProvider;
